import kebabCase from 'lodash/kebabCase';

import paths, {
  PATIENT_WORKSPACE_CP_MATCHING_TAB_QUERY,
  PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY,
  PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_NAME_QUERY,
  PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY,
  PATIENT_WORKSPACE_CP_SCREENING_LIST_QUERY,
  PATIENT_WORKSPACE_FROM_QUERY,
  PATIENT_WORKSPACE_NAV_CTX_QUERY,
  PATIENT_WORKSPACE_NAV_TRIAL_ID_QUERY,
  PATIENT_WORKSPACE_NAV_TRIAL_TAB_QUERY,
  PatientWorkspaceNavCtx,
  type PatientWorkspaceScreeningListReturn,
} from '~/routes/paths';
import { careGapQueryParams } from '~/routes/timeScreeningPage/matchingCarePathways/matchingCarePathways.helpers';
import { PatientTrackerStateId } from '~/store/api/types/patientTrackerStore';
import { MatchingTab } from '~/store/timeScreening/constants';

/**
 * Where the user opened the unified patient workspace from. Lives only in the URL so breadcrumbs,
 * top-nav highlighting, and the evaluation-mode default agree after refresh or a shared link.
 */
export type PatientWorkspaceOrigin =
  | { kind: 'patientTracker'; page: PatientTrackerStateId; trialId?: string }
  | { kind: 'siteTrial'; trialId?: string; tab?: string }
  | { kind: 'carePathwayHub' }
  | { kind: 'carePathwayQueue'; pathwayId: string; pathwayName?: string; tab?: string }
  | {
      kind: 'carePathwayScreening';
      list?: PatientWorkspaceScreeningListReturn;
      matchingTab?: string;
      filterParams: Record<string, string>;
    }
  | { kind: 'none' };

/** Top-nav group highlighted from a workspace origin. `trials` covers tracker lists other than Match Review. */
export type PatientWorkspaceTopNavSection = 'care-pathways' | 'match-review' | 'trials';

const trackerPages = new Set<string>(Object.values(PatientTrackerStateId));
const screeningFilterKeys = Object.keys(careGapQueryParams);

function toParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function readParam(params: URLSearchParams, key: string): string | undefined {
  return params.get(key)?.trim() || undefined;
}

function compact(values: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value)) as Record<string, string>;
}

/** Care-pathway origin from queue or screening params; `navCtx` itself is supplied by the caller. */
function decodeCarePathwayOrigin(search: string): PatientWorkspaceOrigin {
  const params = toParams(search);
  const pathwayId = readParam(params, PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY);
  if (pathwayId) {
    return {
      kind: 'carePathwayQueue',
      pathwayId,
      pathwayName: readParam(params, PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_NAME_QUERY),
      tab: readParam(params, PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY),
    };
  }

  const rawList = readParam(params, PATIENT_WORKSPACE_CP_SCREENING_LIST_QUERY);
  const list = rawList === 'matching' || rawList === 'screened' ? rawList : undefined;
  const matchingTab = readParam(params, PATIENT_WORKSPACE_CP_MATCHING_TAB_QUERY);
  const filterParams = compact(Object.fromEntries(screeningFilterKeys.map((key) => [key, readParam(params, key)])));
  if (list || matchingTab || filterParams.pathwayId) {
    return { kind: 'carePathwayScreening', list, matchingTab, filterParams };
  }

  return { kind: 'carePathwayHub' };
}

export function decodePatientWorkspaceOrigin(search: string): PatientWorkspaceOrigin {
  const params = toParams(search);
  const trialId = readParam(params, PATIENT_WORKSPACE_NAV_TRIAL_ID_QUERY);
  const from = readParam(params, PATIENT_WORKSPACE_FROM_QUERY);
  if (from) {
    const page = trackerPages.has(from) ? (from as PatientTrackerStateId) : PatientTrackerStateId.ALL_PATIENTS_PAGE;
    return { kind: 'patientTracker', page, trialId };
  }

  switch (readParam(params, PATIENT_WORKSPACE_NAV_CTX_QUERY)) {
    case PatientWorkspaceNavCtx.SITE_TRIAL:
      return { kind: 'siteTrial', trialId, tab: readParam(params, PATIENT_WORKSPACE_NAV_TRIAL_TAB_QUERY) };
    case PatientWorkspaceNavCtx.MATCH_REVIEW:
      return { kind: 'patientTracker', page: PatientTrackerStateId.MATCH_REVIEW_PAGE };
    case PatientWorkspaceNavCtx.CARE_PATHWAYS:
      return decodeCarePathwayOrigin(search);
    default:
      return { kind: 'none' };
  }
}

/** Query params merged into a patient workspace URL; inverse of {@link decodePatientWorkspaceOrigin}. */
export function encodePatientWorkspaceOrigin(origin: PatientWorkspaceOrigin): Record<string, string> {
  switch (origin.kind) {
    case 'patientTracker':
      return compact({
        [PATIENT_WORKSPACE_FROM_QUERY]: origin.page,
        [PATIENT_WORKSPACE_NAV_TRIAL_ID_QUERY]: origin.trialId,
      });
    case 'siteTrial':
      return compact({
        [PATIENT_WORKSPACE_NAV_CTX_QUERY]: PatientWorkspaceNavCtx.SITE_TRIAL,
        [PATIENT_WORKSPACE_NAV_TRIAL_ID_QUERY]: origin.trialId,
        [PATIENT_WORKSPACE_NAV_TRIAL_TAB_QUERY]: origin.tab,
      });
    case 'carePathwayHub':
      return { [PATIENT_WORKSPACE_NAV_CTX_QUERY]: PatientWorkspaceNavCtx.CARE_PATHWAYS };
    case 'carePathwayQueue':
      return compact({
        [PATIENT_WORKSPACE_NAV_CTX_QUERY]: PatientWorkspaceNavCtx.CARE_PATHWAYS,
        [PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_ID_QUERY]: origin.pathwayId,
        [PATIENT_WORKSPACE_CP_QUEUE_PATHWAY_NAME_QUERY]: origin.pathwayName,
        [PATIENT_WORKSPACE_CP_QUEUE_TAB_QUERY]: origin.tab,
      });
    case 'carePathwayScreening':
      return compact({
        [PATIENT_WORKSPACE_NAV_CTX_QUERY]: PatientWorkspaceNavCtx.CARE_PATHWAYS,
        ...origin.filterParams,
        [PATIENT_WORKSPACE_CP_SCREENING_LIST_QUERY]: origin.list,
        [PATIENT_WORKSPACE_CP_MATCHING_TAB_QUERY]: origin.matchingTab,
      });
    case 'none':
      return {};
  }
}

/** Query params preserved when redirecting legacy eval-only URLs to unified `/patient/...`. */
export function buildCarePathwayRedirectSearchParams(legacySearch: string): Record<string, string> {
  return encodePatientWorkspaceOrigin(decodeCarePathwayOrigin(legacySearch));
}

export function isCarePathwayOrigin(origin: PatientWorkspaceOrigin): boolean {
  return (
    origin.kind === 'carePathwayHub' || origin.kind === 'carePathwayQueue' || origin.kind === 'carePathwayScreening'
  );
}

/** Top-nav group to highlight on the patient workspace; undefined falls back to pathname rules. */
export function getPatientWorkspaceOriginTopNavSection(
  origin: PatientWorkspaceOrigin,
): PatientWorkspaceTopNavSection | undefined {
  if (isCarePathwayOrigin(origin)) {
    return 'care-pathways';
  }
  if (origin.kind === 'patientTracker') {
    return origin.page === PatientTrackerStateId.MATCH_REVIEW_PAGE ? 'match-review' : 'trials';
  }
  if (origin.kind === 'siteTrial') {
    return 'trials';
  }
  return undefined;
}

/** Screening list location for the "Screen" breadcrumb. */
export function getCarePathwayScreeningLocation(
  origin: Extract<PatientWorkspaceOrigin, { kind: 'carePathwayScreening' }>,
): { pathname: string; search: string } {
  const query = new URLSearchParams(origin.filterParams).toString();
  const search = query ? `?${query}` : '';
  if (origin.list === 'screened') {
    return { pathname: paths.SCREENING_CARE_PATHWAYS_SCREENED, search };
  }

  const matchingTab = origin.matchingTab || kebabCase(MatchingTab.UNSCREENED.toLowerCase());
  return { pathname: paths.SCREENING_CARE_PATHWAYS_MATCHING.replace(':tab?', matchingTab), search };
}
